import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Heading from '@tiptap/extension-heading';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Typography from '@tiptap/extension-typography';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { SimpleEditorToolbar } from './toolbar/simple-editor-toolbar';
import { cn } from '@/lib/utils';

interface SimpleEditorProps {
  content?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  className?: string;
  editable?: boolean;
  showToolbar?: boolean;
  enableAI?: boolean;
}

export function SimpleEditor({
  content = '',
  onChange,
  placeholder = 'Start typing...',
  className,
  editable = true,
  showToolbar = true,
  enableAI = true,
}: SimpleEditorProps) {
  const [isFocused, setIsFocused] = useState(false);

  const baseExtensions = [
    StarterKit.configure({
      bulletList: false,
      orderedList: false,
      listItem: false,
      heading: false,
    }),
    Heading.configure({
      levels: [1, 2, 3, 4, 5, 6],
    }),
    BulletList.configure({
      HTMLAttributes: {
        class: 'tiptap-bullet-list',
      },
    }),
    OrderedList.configure({
      HTMLAttributes: {
        class: 'tiptap-ordered-list',
      },
    }),
    ListItem.configure({
      HTMLAttributes: {
        class: 'tiptap-list-item',
      },
    }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'tiptap-link',
      },
    }),
    Underline,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Highlight.configure({
      multicolor: true,
    }),
    Typography,
    Superscript,
    Subscript,
    Image.configure({
      HTMLAttributes: {
        class: 'tiptap-image',
      },
    }),
    Placeholder.configure({
      placeholder,
    }),
  ];

  const editor = useEditor({
    extensions: baseExtensions,
    content,
    editable,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
          'prose-headings:font-semibold prose-headings:tracking-tight',
          'prose-p:leading-relaxed prose-p:text-foreground',
          'prose-li:text-foreground prose-strong:text-foreground',
          'prose-code:text-foreground prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded',
          'prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground',
          'prose-hr:border-border',
          'min-h-[150px] max-w-none'
        ),
      },
    },
  });

  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  if (!editor) {
    return (
      <div className={cn('flex items-center justify-center p-8', className)}>
        <div className="text-muted-foreground">Loading editor...</div>
      </div>
    );
  }

  return (
    <div className={cn('simple-editor flex flex-col h-full overflow-hidden', className)}>
      {showToolbar && (
        <div className="sticky top-0 z-10 flex-shrink-0">
          <SimpleEditorToolbar 
            editor={editor} 
            showAI={enableAI}
            className={cn(
              'bg-background/60 backdrop-blur-md border-b border-border/30',
              isFocused && 'border-primary/30 bg-background/70'
            )}
          />
        </div>
      )}
      <div className={cn(
        'flex-1 overflow-y-auto overflow-x-hidden transition-colors',
        isFocused && 'bg-background/30'
      )}>
        <EditorContent 
          editor={editor} 
          className="simple-editor-content px-4 py-4 min-h-full"
        />
      </div>
    </div>
  );
}
